import { useLoaderData } from "react-router";
import {
  createAccountDMsReviewPageURL,
  createAccountFavoritePostsPageURL,
  createAccountFavoriteProfilesPageURL,
  createAccountImportKeysPageURL,
  createAccountNotificationsPageURL,
  createAccountPasswordChangePageURL,
  createAccountsPageURL,
  createProfileLinkRequestsPageURL,
} from "#lib/urls";
import { fetchAccount } from "#api/account";
import { IAccount } from "#entities/account";
import { PageSkeleton, createAccountPageLoader } from "#components/pages";
import { KemonoLink } from "#components/links";
import { Timestamp } from "#components/dates";

interface IProps {
  account: IAccount;
  notificationsCount: number;
}

export function AccountPage() {
  const { account, notificationsCount } = useLoaderData() as IProps;
  const { role, username, created_at } = account;
  const title = "Your account details";
  const heading = "Your Account Details";

  return (
    <PageSkeleton name="account" title={title} heading={heading}>
      <div className="account-view">
        <div className="account-view__header">
          <span className="account-view__greeting">
            Hello,{" "}
            <span className="account-view__identity">{username}</span>
          </span>

          <div className="account-view__info">
            Joined <Timestamp time={created_at} /> |{" "}
            <span className="account-view__role">{role}</span>
          </div>
        </div>

        <div className="def-list__section">
          Favorites:
          <ul>
            <li>
              <KemonoLink
                url={String(createAccountFavoriteProfilesPageURL())}
                isNoop={false}
              >
                Profiles
              </KemonoLink>
            </li>
            <li>
              <KemonoLink
                url={String(createAccountFavoritePostsPageURL())}
                isNoop={false}
              >
                Posts
              </KemonoLink>
            </li>
          </ul>
        </div>

        <p className="def-list__section">
          Notifications:
          <span>
            <KemonoLink
              url={String(createAccountNotificationsPageURL())}
              isNoop={false}
            >
              {notificationsCount}
            </KemonoLink>
          </span>
        </p>

        <p className="def-list__section">
          <KemonoLink
            url={String(createAccountImportKeysPageURL())}
            isNoop={false}
          >
            Keys
          </KemonoLink>
        </p>

        <p className="def-list__section">
          <KemonoLink
            url={String(createAccountDMsReviewPageURL())}
            isNoop={false}
          >
            Review DMs
          </KemonoLink>
        </p>

        <p className="def-list__section">
          <KemonoLink url="/account/favorites/export">Export favorites</KemonoLink>
        </p>

        <p className="def-list__section">
          <KemonoLink url="/account/change_username">Change username</KemonoLink>
        </p>

        <p className="def-list__section">
          <KemonoLink
            url={String(createAccountPasswordChangePageURL())}
            isNoop={false}
          >
            Change password
          </KemonoLink>
        </p>

        <div className="mod-actions">
          {["moderator", "administrator"].includes(role) ? (
            <div>
              <h6>Moderator actions</h6>
              <KemonoLink url={String(createProfileLinkRequestsPageURL())}>Creator link requests</KemonoLink>
            </div>
          ) : undefined}

          {role == "administrator" ? (
            <div>
              <h6>Administrator actions</h6>
              <KemonoLink url={String(createAccountsPageURL())}>Manage accounts</KemonoLink>
            </div>
          ) : undefined}
        </div>
      </div>
    </PageSkeleton>
  );
}

export const loader = createAccountPageLoader(
  async function loader(): Promise<IProps> {
    const { props } = await fetchAccount();
    const { account, currentPage, notifications_count, title } = props;

    return {
      account,
      notificationsCount: notifications_count,
    };
  }
);
